// ============================================================================
// JARVISTRAVEL - EARLY ACCESS NOTICE (JAR-34)
// Shown in place of the signup form while self-signup is closed.
// ============================================================================

import { Mail, ArrowRight } from 'lucide-react';
import { APP_CONFIG } from '@/app/constants';
import { LogoLockup } from '@/app/components/shared/Logo';
import { WAITLIST_URL } from '@/app/utils/earlyAccess';

interface EarlyAccessNoticeProps {
  onSignIn: () => void;
}

export function EarlyAccessNotice({ onSignIn }: EarlyAccessNoticeProps) {
  return (
    <div className="min-h-[100dvh] bg-bg flex flex-col">
      {/* Same logo treatment as the sign-in screen — someone bounced here from
          "Sign up" should still feel like they're in the same place. */}
      <div className="pt-12 pb-8 px-6 text-center">
        {/* The real mark, not a lucide glyph in a tile (JAR-890). This screen
            says it wants "the same logo treatment as the sign-in screen", and
            until now it disagreed with it.

            Still an <h1>, with the mark inside. The heading WAS the brand name,
            so the lockup replaces the text rather than sitting above it —
            dropping the <h1> would take the page's only top-level heading from
            screen-reader users to gain a picture, so `title` gives the heading
            its accessible name and it is announced once. */}
        <h1 className="mb-1">
          <LogoLockup
            className="h-10 w-auto mx-auto text-ateneo dark:text-moonlight"
            title={APP_CONFIG.name}
          />
        </h1>
        <p className="text-muted mt-1">Early Access</p>
      </div>

      <div className="flex-1 px-6 pb-8">
        <div className="bg-surface border border-border rounded-3xl shadow-xl shadow-black/5 p-6 max-w-md mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-ink">Invite only for now</h2>
            <p className="text-body mt-3">
              We're letting travelers in a few at a time so we can get it right.
              Join the waitlist and we'll send you an invite.
            </p>
          </div>

          {/* Leaves the app for another property, so open a new tab and keep the
              user's place here; rel closes the reverse-tabnabbing hole that
              target="_blank" opens. */}
          <a
            href={WAITLIST_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-lg bg-brand px-6 py-3.5 text-lg lg:px-5 lg:py-2.5 lg:text-base font-semibold text-brand-fg shadow-sm transition-all duration-150 hover:bg-brand/90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            Join the waitlist
            <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
          </a>

          <div className="mt-6 flex items-start gap-3 rounded-2xl bg-surface-raised p-4">
            <Mail className="w-5 h-5 text-muted shrink-0 mt-0.5" strokeWidth={1.75} />
            <p className="text-sm text-body">
              Already been invited? Open the link in your invitation email to set
              up your account.
            </p>
          </div>
        </div>

        <p className="text-center mt-6 text-muted">
          Already have an account?{' '}
          <button onClick={onSignIn} className="text-accent font-semibold hover:opacity-80">Sign in</button>
        </p>
      </div>
    </div>
  );
}

export default EarlyAccessNotice;
